import type { ReactNode, RefObject } from "react";
import { uploadDocumentWithAI, uploadDocumentOnly, UploadZone } from "../../../lib/resumeUpload.js";
import { useToast } from "../../../context/ToastContext.js";
import { useVirtualHr } from "../../../lib/virtualHr.js";
import { errMsg } from "../../../lib/api.js";
import { SmartBitPanel, useSmartBitPanel } from "../../SmartBitPanel.js";
import { DocVersionHistory } from "../../DocVersionHistory.js";
import type { LightboxTarget } from "../../DocumentLightbox.js";
import { Field, FieldGrid, CollapsibleSection } from "../sectionShared.js";
import { DocumentLockedNotice } from "../../onboarding/DocumentLockedNotice.js";
import { AskForClearerButton } from "../../onboarding/AskForClearerButton.js";

type InputProps = { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled?: boolean; style: React.CSSProperties };

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  canView: (field: string) => boolean;
  inputProps: (field: string, value: string, onChange: (v: string) => void) => InputProps;
  approvalNote: (field: string) => ReactNode;
  pendingBadge: (field: string) => ReactNode;
  isIfscInvalid: (v: string) => boolean;
  isCreate: boolean;
  isHR: boolean;
  employeeId: string | null;
  ensureEmployee: () => Promise<string | null>;
  setSilentError: (v: string) => void;
  setOcrName: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  bumpDocRefresh: (type: string) => void;
  docRefreshKeys: Record<string, number>;
  setVerifyTarget: (t: LightboxTarget) => void;
  /** HR verified the bank document and the viewer is not HR: view only. */
  bankDocLocked: boolean;
  bankAccountNumber: string; setBankAccountNumber: (v: string) => void;
  bankIfscCode: string; setBankIfscCode: (v: string) => void;
  bankName: string; setBankName: (v: string) => void;
  bankAccountHolderName: string; setBankAccountHolderName: (v: string) => void;
}

const BANK_FIELD_LABELS: Record<string, string> = {
  bankName: "Bank name", accountNumber: "Account number", ifscCode: "IFSC code", branchName: "Branch", accountHolderName: "Account holder",
};

// What was read from the cheque or passbook, for HR to check before pressing Verify.
function describeBank(fields: Record<string, string | null>): { label: string; value: string }[] {
  return Object.entries(BANK_FIELD_LABELS).flatMap(([key, label]) => {
    const value = fields[key];
    return typeof value === "string" && value ? [{ label, value }] : [];
  });
}

export function BankDetailsSection({
  sectionRef, canView, inputProps, approvalNote, pendingBadge, isIfscInvalid, isCreate, isHR,
  employeeId, ensureEmployee, setSilentError, setOcrName, bumpDocRefresh, docRefreshKeys, setVerifyTarget, bankDocLocked,
  bankAccountNumber, setBankAccountNumber, bankIfscCode, setBankIfscCode, bankName, setBankName,
  bankAccountHolderName, setBankAccountHolderName,
}: Props) {
  const panel = useSmartBitPanel();
  const vhr = useVirtualHr();
  const toast = useToast();

  const applyBank = (fields: Record<string, string | null>) => {
    if (fields["accountNumber"]) setBankAccountNumber(String(fields["accountNumber"]));
    if (fields["ifscCode"]) setBankIfscCode(String(fields["ifscCode"]));
    if (fields["bankName"]) setBankName(String(fields["bankName"]));
    if (fields["accountHolderName"]) {
      setBankAccountHolderName(String(fields["accountHolderName"]));
      setOcrName(prev => ({ ...prev, bank: String(fields["accountHolderName"]) }));
    }
  };

  // The file is checked to be a cheque leaf or a passbook before it is stored. HR then reviews what
  // was read and presses Verify; an employee's details fill in straight away, as before.
  const uploadBank = async (f: File): Promise<void> => {
    const eid = employeeId ?? await ensureEmployee();
    if (!eid) { setSilentError("Fill First Name and Work Email before uploading documents."); return; }
    // Virtual HR switched off for this document: store the file, no check and no extraction.
    if (!vhr.isDocEnabled("BANK_DOCUMENT")) {
      try {
        await uploadDocumentOnly(eid, f, "BANK_DOCUMENT", { skipVerify: true });
        toast.show("Document uploaded.", "success");
      } catch (e) {
        toast.show(errMsg(e) ?? "Could not upload the document", "error");
      }
      bumpDocRefresh("BANK_DOCUMENT");
      return;
    }
    await panel.run({ label: "Bank document", file: f, retry: uploadBank }, async ({ onVerdict, onReview }) => {
      let versionId = "";
      const fields = await uploadDocumentWithAI(eid, f, "BANK_DOCUMENT", "BANK_DOCUMENT", { onVerdict, onUploaded: (u) => { versionId = u.versionId; } });
      bumpDocRefresh("BANK_DOCUMENT");
      if (isHR) onReview({ versionId, fields: describeBank(fields), onVerified: () => { applyBank(fields); bumpDocRefresh("BANK_DOCUMENT"); } });
      else applyBank(fields);
    });
  };

  return (
    <CollapsibleSection id="bank" sectionRef={sectionRef} title="Bank Details">
      <FieldGrid cols="auto" gap="md">
        {(isCreate || canView("bankAccountNumber")) && (
          <Field label={<>Account Number{approvalNote("bankAccountNumber")}</>}>
            <input {...inputProps("bankAccountNumber", bankAccountNumber, setBankAccountNumber)} />
            {pendingBadge("bankAccountNumber")}
          </Field>
        )}
        {(isCreate || canView("bankIfscCode")) && (
          <Field label={<>IFSC Code{approvalNote("bankIfscCode")}</>}>
            <input {...inputProps("bankIfscCode", bankIfscCode, setBankIfscCode)} placeholder="HDFC0000123"
              style={{ ...inputProps("bankIfscCode", bankIfscCode, setBankIfscCode).style, ...(isIfscInvalid(bankIfscCode) ? { borderColor: "var(--color-error)" } : {}) }} />
            {isIfscInvalid(bankIfscCode) && <div style={{ fontSize: 11, color: "var(--color-error)", marginTop: 3 }}>Format: HDFC0001234 (4 letters · 0 · 6 alphanumeric)</div>}
            {pendingBadge("bankIfscCode")}
          </Field>
        )}
        {(isCreate || canView("bankName")) && (
          <Field label="Bank Name">
            <input {...inputProps("bankName", bankName, setBankName)} placeholder="HDFC Bank" />
          </Field>
        )}
        {(isCreate || canView("bankAccountHolderName")) && (
          <Field label="Account Holder Name">
            <input {...inputProps("bankAccountHolderName", bankAccountHolderName, setBankAccountHolderName)} />
          </Field>
        )}
      </FieldGrid>
      <div style={{ marginTop: "16px" }}>
        <p style={{ fontSize: "12px", fontWeight: 600, margin: "0 0 6px", color: "var(--color-surface-600)" }}>Bank Document</p>
        {bankDocLocked ? <DocumentLockedNotice /> : (
          <UploadZone
            onFile={uploadBank}
            label="Upload cancelled cheque or passbook"
            accept="image/*,.pdf"
          />
        )}
        <SmartBitPanel panel={panel} />
        {employeeId && (
          <DocVersionHistory
            employeeId={employeeId} docType="BANK_DOCUMENT" isHR={isHR}
            refreshKey={docRefreshKeys["BANK_DOCUMENT"] ?? 0} onVerify={setVerifyTarget}
          />
        )}
        <AskForClearerButton employeeId={employeeId} docType="BANK_DOCUMENT" docLabel="bank document" isHR={isHR} setVerifyTarget={setVerifyTarget} />
      </div>
    </CollapsibleSection>
  );
}
